import { Form, Formik } from 'formik';
import { INPUT_AUTOCOMPLETE, INPUT_TYPE } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';

import { Alert } from '../alert';
import { Box, Col, Row } from '../grid';
import { Button } from '../button';
import { FormikTextInput } from '../input';
import { logger } from '../../utils/logger';
import { P, Subheading } from '../typography';
import { useSchema } from '../../hooks/use-schema';

const defaultErrorMessage = 'Sorry, a problem occurred whilst verifying the gift card details. Please try again.';

const CheckGiftCardBalanceForm = () => {
  const { OrderStore } = useRootStore();
  const validationSchema = useSchema('checkGiftCardBalance');

  return (
    <Formik
      initialValues={{
        cardNumber: '',
        verificationCode: '',
      }}
      validationSchema={validationSchema}
      onSubmit={async ({ cardNumber, verificationCode }, { setSubmitting, setStatus }) => {
        setSubmitting(true);

        try {
          const response = await OrderStore.payment.checkGiftCard(cardNumber, verificationCode);

          if (!response.success) {
            setStatus({
              state: 'error',
              message: response.error,
            });
            return;
          }

          setStatus({ state: 'success', balance: response.balance });
        } catch (error) {
          logger.error('Error Checking Gift Card Balance', { error });
          setStatus({
            state: 'error',
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, status }) => (
        <Form>
          <Subheading as="h1" textAlign="center" mb={2}>
            Check gift card balance
          </Subheading>
          <Row flexWrap="wrap" mb={2}>
            <Col mb={{ xs: 1, lg: 0 }} width={{ xs: 12 / 12, lg: 5 / 12 }}>
              <FormikTextInput
                autoComplete={INPUT_AUTOCOMPLETE.TEXT}
                label="Gift Card Number"
                maxLength={11}
                name="cardNumber"
                type={INPUT_TYPE.TEXT}
              />
            </Col>
            <Col width={{ xs: 6 / 12, lg: 3 / 12 }}>
              <FormikTextInput
                autoComplete={INPUT_AUTOCOMPLETE.TEXT}
                label="Verification Code"
                maxLength={6}
                name="verificationCode"
                type={INPUT_TYPE.TEXT}
              />
            </Col>
            <Col width={{ xs: 12 / 12, lg: 4 / 12 }}>
              <Button
                mt={{ xs: 1, lg: '1.5rem', xl: 1 }}
                isFullWidth
                disabled={isSubmitting}
                isLoading={isSubmitting}
                type="submit"
              >
                Check Balance
              </Button>
            </Col>
          </Row>
          {status?.state === 'success' && (
            <P>
              Balance:{' '}
              {new Intl.NumberFormat('en-AU', {
                style: 'currency',
                currency: process.env.currencyCode,
                currencyDisplay: 'narrowSymbol',
              }).format(status.balance)}
            </P>
          )}
          {status?.state === 'error' && (
            <Box mt={1}>
              <Alert type="error">{status.message || defaultErrorMessage}</Alert>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
};

CheckGiftCardBalanceForm.propTypes = {};

CheckGiftCardBalanceForm.defaultProps = {};

const ConnectedCheckGiftCardBalanceForm = observer(CheckGiftCardBalanceForm);

export { ConnectedCheckGiftCardBalanceForm as CheckGiftCardBalanceForm };
